// Booking-activity (TrainingActivity) admin manager — 0.12.5 Part I.
//
// Ben, 2026-09-07: "there is nowhere to amend what appears in the Activity list." The backend has
// carried full CRUD since the module landed (training-activities.router.ts: GET staff-gated, POST
// / PUT / DELETE all requireRole('admin')); this is the SCREEN only. No backend work, no migration.
//
// Built on the BookingKindsManager pattern deliberately — same list, same modal, same setToast —
// because these two are the same sort of entity: a small soft-delete-only reference list with no
// orphan-cascade model, so neither uses the shared ResourceManager/DeactivateDialog.
//
// ─── WHAT IS DELIBERATELY NOT ON THIS FORM ───────────────────────────────────────────────────
// ⚠ default_activity_mins and default_buffer_mins. Ben ruled 2026-09-04 that they are NOT to be
// wired up: they are populated in production, read by nothing, and the stored values do not
// reflect current practice (every booking defaults to two hours and is adjusted per booking).
// Putting them on a screen invites someone to "fix" them, which would be a REGRESSION, not a fix.
// The full reasoning lives with the columns at schema.prisma:1098-1109. Do not add them.
//
// ⚠ default_long_brief_mins, default_short_brief_mins, is_miscellaneous and course_id are also
// off. That is a JUDGMENT CALL, not one of Ben's rulings, and it is flagged as such: grep finds
// each of them ONLY in training-activities.schemas.ts (the write schemas) — no code reads any of
// them, so leaving them off keeps today's behaviour exactly, whereas surfacing them would be a
// new promise the system does not keep. course_id is additionally opaque, with no Course model
// yet. If Ben wants any of them editable, each is one field on the form below and nothing else.
//
// What IS here: name (required, unique per tenant), activity kind (required — the column is NOT
// NULL with no default), category (free text) and sequence number (the one optional column that
// is genuinely read: training-activities.service.ts:23 orders the list by it, which is the order
// the booking form's Activity dropdown appears in).

import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { C, F, card, Btn, StatusPill, Modal, ErrorText } from './adminUi';
import * as api from '../../lib/admin';

const rowStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderBottom: `1px solid ${C.border}` };
const labelStyle: CSSProperties = { fontWeight: 600, fontSize: 12, color: C.textDim, marginBottom: 3, display: 'block' };
const inputStyle: CSSProperties = { fontFamily: F, fontSize: 13, padding: '6px 8px', border: `1px solid ${C.borderMed}`, borderRadius: 6, background: '#fff', color: C.text, width: '100%' };

// Mirrors the TrainingActivityKind enum (schema.prisma:26-31) — flight | briefing | simulator |
// ground. The column is NOT NULL with no default, so the form always sends one.
const KINDS: api.TrainingActivityKindValue[] = ['flight', 'briefing', 'simulator', 'ground'];
const kindLabel: Record<api.TrainingActivityKindValue, string> = {
  flight: 'Flight', briefing: 'Briefing', simulator: 'Simulator', ground: 'Ground',
};

interface FormState { name: string; activity_kind: api.TrainingActivityKindValue; category: string; sequence_number: string }
const blank: FormState = { name: '', activity_kind: 'flight', category: '', sequence_number: '' };
const toForm = (r: api.AdminTrainingActivity): FormState => ({
  name: r.name,
  activity_kind: r.activity_kind,
  category: r.category ?? '',
  sequence_number: r.sequence_number == null ? '' : String(r.sequence_number),
});

export default function TrainingActivitiesManager({ setToast }: { setToast: (s: string | null) => void }) {
  const [rows, setRows] = useState<api.AdminTrainingActivity[]>([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<{ kind: 'form'; editing: api.AdminTrainingActivity | null } | { kind: 'retire'; row: api.AdminTrainingActivity } | null>(null);

  const load = useCallback(() => { api.listTrainingActivities(includeInactive).then(setRows); }, [includeInactive]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => load();

  // Client-side filter over the loaded rows, matching the visible name + category, as the other
  // admin lists do. This list is expected to grow (one row per lesson on a syllabus).
  const q = search.trim().toLowerCase();
  const shown = q ? rows.filter((r) => `${r.name} ${r.category ?? ''}`.toLowerCase().includes(q)) : rows;

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <h1 style={{ fontSize: 17, margin: 0 }}>Booking activities</h1>
        <label style={{ marginLeft: 16, fontSize: 12.5, color: C.textDim, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} style={{ accentColor: C.accent }} /> include inactive
        </label>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" style={{ ...inputStyle, fontSize: 12.5, padding: '5px 8px', width: 160 }} />
          <Btn kind="primary" onClick={() => setModal({ kind: 'form', editing: null })}>+ New activity</Btn>
        </div>
      </div>
      <p style={{ color: C.textDim, fontSize: 12.5, margin: '0 0 12px' }}>
        What appears in the <b>Activity</b> list on a booking — the lesson, topic or job the booking is for. The activity is
        optional on a booking; the booking type is the required one. Activities are offered in <b>sequence number</b> order,
        then by name. Retiring keeps existing bookings; it just hides the activity from new ones.
      </p>

      {rows.length === 0 ? <div style={{ color: C.textMuted, fontStyle: 'italic', padding: 16 }}>None.</div>
        : shown.length === 0 ? <div style={{ color: C.textMuted, fontStyle: 'italic', padding: 16 }}>No matches.</div>
        : shown.map((r) => (
          <div key={r.id} style={rowStyle}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{r.name} <StatusPill active={r.is_active} /></div>
              <div style={{ fontSize: 12, color: C.textDim, marginTop: 2 }}>
                {[kindLabel[r.activity_kind], r.category, r.sequence_number == null ? null : `#${r.sequence_number}`].filter(Boolean).join(' · ')}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {r.is_active ? <>
                <Btn kind="ghost" onClick={() => setModal({ kind: 'form', editing: r })}>Edit</Btn>
                <Btn kind="danger" onClick={() => setModal({ kind: 'retire', row: r })}>Retire</Btn>
              </> : <Btn kind="accent" onClick={async () => { const res = await api.reactivateTrainingActivity(r.id); if (!res.error) { setToast(`${r.name} reactivated.`); refresh(); } else setToast(res.error); }}>Reactivate</Btn>}
            </div>
          </div>
        ))}

      {modal?.kind === 'form' && (
        <ActivityForm editing={modal.editing} onClose={() => setModal(null)}
          onSaved={(msg) => { setModal(null); setToast(msg); refresh(); }} />
      )}
      {modal?.kind === 'retire' && (
        <RetireDialog row={modal.row} onClose={() => setModal(null)}
          onDone={() => { setModal(null); setToast(`${modal.row.name} retired.`); refresh(); }} />
      )}
    </div>
  );
}

function ActivityForm({ editing, onClose, onSaved }: { editing: api.AdminTrainingActivity | null; onClose: () => void; onSaved: (msg: string) => void }) {
  const initial = editing ? toForm(editing) : blank;
  const [form, setForm] = useState<FormState>(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);

  const submit = async () => {
    setError(null);
    // Mirrors trainingActivityCreateSchema's nameField (min 1, max 100) so the operator is told
    // here rather than by a 400 from the server. The server stays the authority.
    const name = form.name.trim();
    if (!name) { setError('Name is required.'); return; }
    if (name.length > 100) { setError('Name must be 100 characters or fewer.'); return; }

    // sequence_number is optional; blank clears it. The write schema takes a whole number >= 0.
    const seqRaw = form.sequence_number.trim();
    let sequenceNumber: number | null = null;
    if (seqRaw) {
      const n = Number(seqRaw);
      if (!Number.isInteger(n) || n < 0) { setError('Sequence number must be a whole number of 0 or more.'); return; }
      sequenceNumber = n;
    }

    setBusy(true);
    const body: Record<string, unknown> = { name, activity_kind: form.activity_kind };
    // Present-versus-absent, matching the other admin forms: a blank optional field on CREATE is
    // omitted (leave the column alone), and on EDIT is sent as null so blanking it really clears.
    const category = form.category.trim();
    if (category) body.category = category;
    else if (editing) body.category = null;
    if (sequenceNumber !== null) body.sequence_number = sequenceNumber;
    else if (editing) body.sequence_number = null;

    const res = editing ? await api.updateTrainingActivity(editing.id, body) : await api.createTrainingActivity(body);
    setBusy(false);
    if (res.error) { setError(res.error); return; }
    onSaved(editing ? 'Saved.' : 'Activity created.');
  };

  return (
    <Modal title={editing ? `Edit ${editing.name}` : 'New booking activity'} onClose={onClose} width={460} dirty={dirty}>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Name <span style={{ color: C.red }}>*</span></label>
        <input value={form.name} maxLength={100} placeholder="e.g. Effects of Controls, Circuits, Nav 3" onChange={(e) => setForm({ ...form, name: e.target.value })} style={inputStyle} />
        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>Up to 100 characters, and unique within your organisation.</div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Kind <span style={{ color: C.red }}>*</span></label>
        <select value={form.activity_kind} onChange={(e) => setForm({ ...form, activity_kind: e.target.value as api.TrainingActivityKindValue })} style={inputStyle}>
          {KINDS.map((k) => <option key={k} value={k}>{kindLabel[k]}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Category</label>
        <input value={form.category} placeholder="Optional — e.g. RPL, PPL, CPL" onChange={(e) => setForm({ ...form, category: e.target.value })} style={inputStyle} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Sequence number</label>
        <input type="number" min={0} step={1} value={form.sequence_number} placeholder="Optional" onChange={(e) => setForm({ ...form, sequence_number: e.target.value })} style={{ ...inputStyle, width: 120 }} />
        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>Sets where this activity sits in the booking form's list. Leave blank to sort by name.</div>
      </div>
      <ErrorText>{error}</ErrorText>
      <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
        <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
        <Btn kind="primary" disabled={busy} onClick={submit}>{editing ? 'Save' : 'Create'}</Btn>
      </div>
    </Modal>
  );
}

// ⚠ RETIRE IS A SOFT DELETE and nothing else. Verified at training-activities.service.ts:104 —
// DELETE sets is_active:false and audits the row; there is no prisma.trainingActivity.delete
// anywhere in the module. The wording below says "retire", never "delete", to match.
//
// ⚠ THERE IS NO IMPACT COUNT HERE. Booking kinds show one because /api/booking-kinds/:id/impact
// exists; training activities have no such route and 0.12.5 adds no backend. So this dialog states
// the consequence honestly instead of implying a count it cannot get. That consequence is real and
// checkable: OpsBoard.tsx:185 fetches /api/training-activities WITHOUT include_inactive, so a
// retired activity drops out of the board's activity map and taskRowLabel (bookingLabel.ts:80)
// then resolves nothing for it and falls through to the booking type. The booking's activity_id is
// untouched, so reactivating brings the name straight back.
function RetireDialog({ row, onClose, onDone }: { row: api.AdminTrainingActivity; onClose: () => void; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setBusy(true); setError(null);
    const res = await api.retireTrainingActivity(row.id);
    setBusy(false);
    if (res.error) { setError(res.error); return; }
    onDone();
  };

  return (
    <Modal title={`Retire ${row.name}`} onClose={onClose} width={440}>
      <p style={{ fontSize: 13, color: C.text, margin: '0 0 8px' }}>
        Retiring hides <b>{row.name}</b> from the Activity list so it cannot be picked for new bookings. Nothing is deleted.
      </p>
      <p style={{ fontSize: 12.5, color: C.textDim, margin: 0 }}>
        Bookings already using it keep it on record, but the board stops showing the activity name on those chips until it is
        reactivated. Retiring is reversible — tick <i>include inactive</i> to find it again.
      </p>
      <ErrorText>{error}</ErrorText>
      <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
        <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
        <Btn kind="danger" disabled={busy} onClick={confirm}>Retire activity</Btn>
      </div>
    </Modal>
  );
}
